import { ReactNode, Key } from 'react';
import { Link } from 'react-router-dom';
import { cn } from '../../../lib/utils';
import {
  tertiaryText,
  primaryText,
  borderPrimary,
  semiBoldText,
  smallTextSize,
  greenTextColor,
} from '../../../styling/stylingHelper';

/**
 * ResponsiveTable — the table→card gate for mobile.
 *
 * Every domain table ships TWO renderings: the existing desktop table and a
 * stacked card list for phones/tablets. This wrapper encodes the invariant so a
 * task can't forget the gates: `desktop` renders inside `hidden sm:block`,
 * `mobile` inside `sm:hidden`. Desktop (≥sm) is byte-identical to before; the
 * card list only ever appears below `sm`.
 *
 * Compose the mobile slot from {@link MobileCardField} (labeled field) and
 * {@link MobileCardList} (preview + "View All" / "Load more" for long lists).
 * Compatible with the hand-rolled `pages/inventory/inventoryProductMobileCard.tsx`
 * (same label=tertiaryText / value=primaryText anatomy) — MOBILE-7 migrates it.
 *
 * @example
 * <ResponsiveTable
 *   desktop={<OrdersTable rows={rows} />}
 *   mobile={
 *     <MobileCardList
 *       items={rows}
 *       keyOf={(o) => o.id}
 *       viewAllLabel={t('dashboard.seeAll')}
 *       onViewAll={() => navigate('/orderhistory')}
 *       renderCard={(o) => (
 *         <div className="flex">
 *           <MobileCardField label={t('order.supplier')} value={o.supplier} divider />
 *           <MobileCardField label={t('order.spend')} value={fmt(o.spend)} align="right" />
 *         </div>
 *       )}
 *     />
 *   }
 * />
 */
export function ResponsiveTable({
  desktop,
  mobile,
  className,
}: {
  desktop: ReactNode;
  mobile: ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <div className="hidden sm:block">{desktop}</div>
      <div className="sm:hidden">{mobile}</div>
    </div>
  );
}

/**
 * MobileCardField — one labeled field inside a mobile card.
 *
 * Label on top (`tertiaryText`, 12px medium), value below (`primaryText`, 14px
 * semibold) — the anatomy the designer's cards and `inventoryProductMobileCard`
 * both use. `align="right"` right-aligns the pair; `divider` adds a right-hand
 * vertical rule so several fields sit in a flex row with column separators (drop
 * `divider` on the last field).
 */
export function MobileCardField({
  label,
  value,
  align = 'left',
  divider = false,
  className,
}: {
  label: ReactNode;
  value: ReactNode;
  align?: 'left' | 'right';
  divider?: boolean;
  className?: string;
}) {
  return (
    <div
      className={cn(
        'flex flex-col gap-0.5 min-w-0',
        align === 'right' ? 'items-end text-right' : 'items-start text-left',
        divider && cn('border-r pr-4 mr-4', borderPrimary),
        className,
      )}
    >
      <span className={cn(tertiaryText, 'text-xs font-medium leading-[18px] truncate max-w-full')}>{label}</span>
      <span className={cn(primaryText, smallTextSize, semiBoldText, 'leading-5 truncate max-w-full')}>{value}</span>
    </div>
  );
}

/**
 * MobileCardList — renders a card per item with built-in large-list handling.
 *
 * Two modes (caller owns the data + translations — labels are passed in, never
 * hardcoded, per the repo translation rule):
 *  - **Preview** (default): shows the first `previewCount` (default 3) items and,
 *    when there are more, a "View All" affordance — a `viewAllHref` route link or
 *    an `onViewAll` callback. This is the dashboard/summary pattern.
 *  - **Load-more**: pass `onLoadMore` and render the caller's already-paged
 *    `items` in full; a "Load more" button shows while `hasMore` is true. This is
 *    the full-page incremental pattern (see Conversion Playbook → Large lists).
 *    `previewCount` is ignored in this mode.
 */
export function MobileCardList<T>({
  items,
  renderCard,
  keyOf,
  previewCount = 3,
  viewAllHref,
  onViewAll,
  viewAllLabel,
  onLoadMore,
  hasMore = false,
  loadMoreLabel,
  className,
}: {
  items: T[];
  renderCard: (item: T, index: number) => ReactNode;
  keyOf: (item: T, index: number) => Key;
  previewCount?: number;
  viewAllHref?: string;
  onViewAll?: () => void;
  viewAllLabel?: ReactNode;
  onLoadMore?: () => void;
  hasMore?: boolean;
  loadMoreLabel?: ReactNode;
  className?: string;
}) {
  const loadMoreMode = onLoadMore != null;
  const visible = loadMoreMode ? items : items.slice(0, previewCount);
  const showViewAll = !loadMoreMode && items.length > previewCount && (viewAllHref != null || onViewAll != null);

  return (
    <div className={cn('flex flex-col gap-3', className)}>
      {visible.map((item, i) => (
        <div key={keyOf(item, i)}>{renderCard(item, i)}</div>
      ))}

      {showViewAll &&
        (viewAllHref != null ? (
          <Link to={viewAllHref} className={cn(greenTextColor, semiBoldText, smallTextSize, 'self-center py-1')}>
            {viewAllLabel}
          </Link>
        ) : (
          <button
            type="button"
            onClick={onViewAll}
            className={cn(greenTextColor, semiBoldText, smallTextSize, 'self-center py-1')}
          >
            {viewAllLabel}
          </button>
        ))}

      {loadMoreMode && hasMore && (
        <button
          type="button"
          onClick={onLoadMore}
          className={cn(greenTextColor, semiBoldText, smallTextSize, 'self-center py-1')}
        >
          {loadMoreLabel}
        </button>
      )}
    </div>
  );
}

export default ResponsiveTable;
